import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CalendarDays, CheckCircle, IndianRupee, Clock, ShieldAlert, School, MapPin, Navigation, MessageCircle, Upload } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAcademicYearOptions } from '../../utils/academicYear';
import { api } from '../../utils/api';
import { clearSchoolInfoCache } from '../../utils/printHelper';


export default function AcademicSettings() {
  const { academicYear, setAcademicYear, admissionFee, setAdmissionFee, setBrandLogo, setBrandPrimary, setBrandSecondary } = useApp();
  const options = getAcademicYearOptions();
  const [feeInput, setFeeInput] = useState(String(admissionFee || ''));
  const [feeSaved, setFeeSaved] = useState(false);

  // School info
  const [schoolInfo, setSchoolInfo] = useState({ name: '', address: '', phone: '', lat: '', lng: '' });
  const [schoolSaving, setSchoolSaving] = useState(false);
  const [schoolSaved,  setSchoolSaved]  = useState(false);
  const [gpsLoading,   setGpsLoading]   = useState(false);

  // Branding
  const [logoUrl,        setLogoUrl]        = useState('');
  const [logoUploading,  setLogoUploading]  = useState(false);
  const [primaryColor,   setPrimaryColor]   = useState('#6b21a8');
  const [secondaryColor, setSecondaryColor] = useState('#9333ea');
  const logoInputRef = useRef(null);

  // WhatsApp settings
  const [wa, setWa]           = useState({ enabled: false, phoneId: '', token: '' });
  const [waSaving, setWaSaving] = useState(false);
  const [waSaved,  setWaSaved]  = useState(false);
  const [waTest,   setWaTest]   = useState('');
  const [waTesting, setWaTesting] = useState(false);

  useEffect(() => {
    api.getSchoolSettings().then(d => {
      setSchoolInfo({
        name: d.name || '', address: d.address || '', phone: d.phone || '',
        lat: d.lat != null ? String(d.lat) : '', lng: d.lng != null ? String(d.lng) : '',
      });
      setWa({ enabled: !!d.waEnabled, phoneId: d.waPhoneId || '', token: d.waToken || '' });
      if (d.logo)           setLogoUrl(d.logo);
      if (d.primaryColor)   setPrimaryColor(d.primaryColor);
      if (d.secondaryColor) setSecondaryColor(d.secondaryColor);
    }).catch(() => {});
  }, []);

  const captureSchoolGPS = () => {
    if (!navigator.geolocation) return alert('Geolocation not supported in this browser.');
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setSchoolInfo(p => ({ ...p, lat: pos.coords.latitude.toFixed(7), lng: pos.coords.longitude.toFixed(7) }));
        setGpsLoading(false);
      },
      () => { alert('Could not get location. Allow location access and try again.'); setGpsLoading(false); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop();
    setLogoUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const token = JSON.parse(localStorage.getItem('sms_auth') || '{}')?.token || '';
        const res = await fetch('/api/upload-logo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ data: e.target.result, ext }),
        });
        const data = await res.json();
        if (data.url) {
          setLogoUrl(data.url);
          setBrandLogo(data.url);
          await api.saveSchoolSettings({ ...schoolInfo, logo: data.url, waEnabled: wa.enabled, waPhoneId: wa.phoneId, waToken: wa.token, primaryColor, secondaryColor });
          clearSchoolInfoCache();
        } else { alert('Logo upload failed: ' + (data.message || 'Unknown error')); }
        setLogoUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (e) { alert(e.message); setLogoUploading(false); }
  };

  const saveSchoolInfo = async () => {
    setSchoolSaving(true);
    try {
      await api.saveSchoolSettings({ ...schoolInfo, logo: logoUrl, waEnabled: wa.enabled, waPhoneId: wa.phoneId, waToken: wa.token, primaryColor, secondaryColor });
      clearSchoolInfoCache();
      setBrandPrimary(primaryColor);
      setBrandSecondary(secondaryColor);
      document.documentElement.style.setProperty('--brand-primary',   primaryColor);
      document.documentElement.style.setProperty('--brand-secondary', secondaryColor);
      setSchoolSaved(true);
      setTimeout(() => setSchoolSaved(false), 3000);
    } catch (e) { alert(e.message); }
    finally { setSchoolSaving(false); }
  };

  const saveWa = async () => {
    setWaSaving(true);
    try {
      await api.saveSchoolSettings({ ...schoolInfo, logo: logoUrl, waEnabled: wa.enabled, waPhoneId: wa.phoneId, waToken: wa.token, primaryColor, secondaryColor });
      setWaSaved(true);
      setTimeout(() => setWaSaved(false), 3000);
    } catch (e) { alert(e.message); }
    finally { setWaSaving(false); }
  };

  const testWa = async () => {
    if (!waTest.trim()) return alert('Enter a phone number to test.');
    setWaTesting(true);
    try {
      await api.testWhatsApp(waTest);
      alert('✅ Test WhatsApp message sent! Check the phone.');
    } catch (e) { alert('Error: ' + e.message); }
    finally { setWaTesting(false); }
  };

  // Attendance Policy
  const [policy, setPolicy] = useState({
    checkInTime: '09:00', checkOutTime: '17:00', graceMinutes: 15,
    latesForHalfDay: 3, latesForFullAbsent: 6, halfDayLopPercent: 50,
  });
  const [policySaved, setPolicySaved] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(true);

  const loadPolicy = useCallback(async () => {
    try {
      const r = await api.getAttendancePolicy();
      setPolicy({
        checkInTime:       r.checkInTime?.substring(0,5)  || '09:00',
        checkOutTime:      r.checkOutTime?.substring(0,5) || '17:00',
        graceMinutes:      r.graceMinutes      ?? 15,
        latesForHalfDay:   r.latesForHalfDay   ?? 3,
        latesForFullAbsent:r.latesForFullAbsent ?? 6,
        halfDayLopPercent: r.halfDayLopPercent  ?? 50,
      });
    } catch {} finally { setPolicyLoading(false); }
  }, []);

  useEffect(() => { loadPolicy(); }, [loadPolicy]);

  const savePolicy = async () => {
    try {
      await api.saveAttendancePolicy(policy);
      setPolicySaved(true);
      setTimeout(() => setPolicySaved(false), 2500);
    } catch (e) { alert('Error saving policy: ' + e.message); }
  };

  const pf = (k, v) => setPolicy(p => ({ ...p, [k]: v }));

  const saveFee = async () => {
    await setAdmissionFee(feeInput);
    setFeeSaved(true);
    setTimeout(() => setFeeSaved(false), 2000);
  };

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Academic Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Configure the active academic year for the school.</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-purple-50 rounded-lg"><CalendarDays size={20} className="text-purple-600" /></div>
          <div>
            <p className="font-semibold text-gray-800">Academic Year</p>
            <p className="text-xs text-gray-500">Session runs June → April. New year appears after April.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Active Academic Year</label>
          <select
            value={academicYear}
            onChange={e => setAcademicYear(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
          >
            {options.map(yr => (
              <option key={yr} value={yr}>{yr}</option>
            ))}
          </select>
        </div>

        <div className="bg-purple-50 border border-purple-100 rounded-lg px-4 py-3 flex items-center gap-3">
          <CheckCircle size={18} className="text-purple-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-purple-800">Active Year: {academicYear}</p>
            <p className="text-xs text-purple-600 mt-0.5">
              This year is used across Fee Structures, Promotions, Exams, and Reports.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg px-4 py-3 text-xs text-gray-500 space-y-1">
          <p className="font-medium text-gray-600">Academic Year Schedule</p>
          <p>• Session begins: <strong>June</strong></p>
          <p>• Session ends: <strong>April</strong> (following year)</p>
          <p>• Next year option appears automatically after <strong>April</strong></p>
        </div>
      </div>

      {/* Admission Fee */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-amber-50 rounded-lg"><IndianRupee size={20} className="text-amber-600" /></div>
          <div>
            <p className="font-semibold text-gray-800">Admission Fee</p>
            <p className="text-xs text-gray-500">One-time fee collected from every new student at the time of admission.</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">₹</span>
            <input
              type="number"
              min="0"
              value={feeInput}
              onChange={e => { setFeeInput(e.target.value); setFeeSaved(false); }}
              placeholder="0"
              className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <button
            onClick={saveFee}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${feeSaved ? 'bg-green-500 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
          >
            {feeSaved ? '✓ Saved' : 'Save'}
          </button>
        </div>

        {Number(feeInput) > 0 ? (
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 text-sm text-amber-800">
            Every new admission will require payment of <strong>₹{Number(feeInput).toLocaleString('en-IN')}</strong> before the student can be admitted.
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3 text-sm text-gray-500">
            Set an amount above to require admission fee payment during new student registration.
          </div>
        )}
      </div>

      {/* Attendance Policy */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg"><Clock size={20} className="text-blue-600" /></div>
            <div>
              <p className="font-semibold text-gray-800">Attendance Policy</p>
              <p className="text-xs text-gray-500">Set check-in/out times and late rules for salary calculation.</p>
            </div>
          </div>
          <button
            onClick={savePolicy}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${policySaved ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
          >{policySaved ? '✓ Saved' : 'Save Policy'}</button>
        </div>

        {policyLoading ? <p className="text-sm text-gray-400">Loading…</p> : (
          <>
            {/* Working hours */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Working Hours</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Check-in Time</label>
                  <input type="time" value={policy.checkInTime}
                    onChange={e => pf('checkInTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Check-out Time</label>
                  <input type="time" value={policy.checkOutTime}
                    onChange={e => pf('checkOutTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Grace Period (min)</label>
                  <input type="number" min="0" max="60" value={policy.graceMinutes}
                    onChange={e => pf('graceMinutes', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none" />
                </div>
              </div>
            </div>

            {/* Late rules */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert size={14} className="text-orange-500" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Late Arrival Rules</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Lates → Half-Day Cut</label>
                  <input type="number" min="1" max="30" value={policy.latesForHalfDay}
                    onChange={e => pf('latesForHalfDay', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none" />
                  <p className="text-xs text-gray-400 mt-1">Every N lates = 0.5 day LOP</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Lates → Full Absent</label>
                  <input type="number" min="1" max="30" value={policy.latesForFullAbsent}
                    onChange={e => pf('latesForFullAbsent', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-300 focus:outline-none" />
                  <p className="text-xs text-gray-400 mt-1">N+ lates = full LOP day</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Half-Day LOP %</label>
                  <input type="number" min="0" max="100" value={policy.halfDayLopPercent}
                    onChange={e => pf('halfDayLopPercent', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none" />
                  <p className="text-xs text-gray-400 mt-1">% of daily rate deducted</p>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-800 space-y-1">
              <p>✓ Check-in expected by <strong>{policy.checkInTime}</strong> (grace: {policy.graceMinutes} min → deadline {(() => { const [h,m]=policy.checkInTime.split(':').map(Number); const t=h*60+m+Number(policy.graceMinutes); return `${String(Math.floor(t/60)).padStart(2,'0')}:${String(t%60).padStart(2,'0')}`; })()})</p>
              <p>✓ Every <strong>{policy.latesForHalfDay}</strong> late arrivals deduct <strong>{policy.halfDayLopPercent}%</strong> of one day's salary</p>
              <p>✓ <strong>{policy.latesForFullAbsent}+</strong> late arrivals in a month treated as a full absent day (LOP)</p>
            </div>
          </>
        )}
      </div>

      {/* School Information */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-green-50 rounded-lg"><School size={20} className="text-green-600" /></div>
          <div>
            <p className="font-semibold text-gray-800">School Information</p>
            <p className="text-xs text-gray-500">Name, address and phone shown on all printed documents.</p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
          <input value={schoolInfo.name} onChange={e => setSchoolInfo(p => ({ ...p, name: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
            placeholder="Smart Kids Future School" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <input value={schoolInfo.address} onChange={e => setSchoolInfo(p => ({ ...p, address: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
            placeholder="Near: Ayyappa Temple, Dharmaram, Dist: Peddapalli" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
          <input value={schoolInfo.phone} onChange={e => setSchoolInfo(p => ({ ...p, phone: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
            placeholder="+91 98765 43210" />
        </div>

        {/* School Location (lat/lng) */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <MapPin size={14} className="text-green-600" /> School Location (GPS)
            </label>
            <button type="button" onClick={captureSchoolGPS} disabled={gpsLoading}
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium border border-green-400 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-50 transition-colors">
              <Navigation size={12} /> {gpsLoading ? 'Getting location…' : 'Use My Location'}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Latitude</label>
              <input value={schoolInfo.lat} onChange={e => setSchoolInfo(p => ({ ...p, lat: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                placeholder="e.g. 18.5204" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Longitude</label>
              <input value={schoolInfo.lng} onChange={e => setSchoolInfo(p => ({ ...p, lng: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                placeholder="e.g. 73.8567" />
            </div>
          </div>
          {schoolInfo.lat && schoolInfo.lng ? (
            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
              <CheckCircle size={11} /> {parseFloat(schoolInfo.lat).toFixed(5)}, {parseFloat(schoolInfo.lng).toFixed(5)} — used to calculate pickup point distances
            </p>
          ) : (
            <p className="text-xs text-amber-500 mt-1">Set the school GPS location to enable distance display on the Routes page.</p>
          )}
        </div>

        {/* Branding — logo + colors */}
        <div className="border-t border-gray-100 pt-4 space-y-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Branding</p>

          {/* Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">School Logo</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-gray-50 overflow-hidden">
                {logoUrl
                  ? <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                  : <Upload size={22} className="text-gray-300" />
                }
              </div>
              <div>
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  className="hidden" onChange={e => uploadLogo(e.target.files[0])} />
                <button onClick={() => logoInputRef.current?.click()} disabled={logoUploading}
                  className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50">
                  {logoUploading ? 'Uploading…' : 'Upload Logo'}
                </button>
                <p className="text-xs text-gray-400 mt-1">PNG, JPG, SVG or WebP · Used in print headers & sidebar</p>
              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                  className="w-10 h-10 rounded-lg cursor-pointer border border-gray-300" />
                <input type="text" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                  maxLength={7} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-300" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Sidebar active links · Print headers</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)}
                  className="w-10 h-10 rounded-lg cursor-pointer border border-gray-300" />
                <input type="text" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)}
                  maxLength={7} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-300" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Accent elements · Badges</p>
            </div>
          </div>
        </div>

        <button onClick={saveSchoolInfo} disabled={schoolSaving}
          className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors">
          {schoolSaved ? <><CheckCircle size={15} /> Saved!</> : schoolSaving ? 'Saving…' : 'Save School Info'}
        </button>
      </div>

      {/* WhatsApp Notifications */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg"><MessageCircle size={20} className="text-green-600" /></div>
            <div>
              <p className="font-semibold text-gray-800">WhatsApp Notifications</p>
              <p className="text-xs text-gray-500">Send automated WhatsApp messages via WhatsApp Cloud API (Meta).</p>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <span className="text-sm font-medium text-gray-600">{wa.enabled ? 'Enabled' : 'Disabled'}</span>
            <div
              onClick={() => setWa(w => ({ ...w, enabled: !w.enabled }))}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${wa.enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${wa.enabled ? 'translate-x-5' : ''}`} />
            </div>
          </label>
        </div>

        <div className="bg-green-50 border border-green-100 rounded-lg p-4 text-xs text-green-800 space-y-1">
          <p className="font-semibold">Sends WhatsApp messages for:</p>
          <p>• Absent attendance alerts (immediately when marked)</p>
          <p>• Fee payment confirmations</p>
          <p>• Fee due reminders (daily at 6 AM)</p>
          <p>• Birthday wishes (6 AM on student's birthday)</p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">API Configuration</label>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number ID</label>
              <input
                value={wa.phoneId}
                onChange={e => setWa(w => ({ ...w, phoneId: e.target.value }))}
                placeholder="e.g. 123456789012345"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-mono"
              />
              <p className="text-xs text-gray-400 mt-1">From Meta Business → WhatsApp → API Setup → Phone Number ID</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
              <input
                type="password"
                value={wa.token}
                onChange={e => setWa(w => ({ ...w, token: e.target.value }))}
                placeholder="Paste your permanent access token"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-mono"
              />
              <p className="text-xs text-gray-400 mt-1">Generate a permanent token in Meta Business Manager → System Users</p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Test Message</label>
          <div className="flex gap-2">
            <input
              value={waTest}
              onChange={e => setWaTest(e.target.value)}
              placeholder="Enter phone number (e.g. 9876543210)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
            />
            <button onClick={testWa} disabled={waTesting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 whitespace-nowrap">
              {waTesting ? 'Sending…' : 'Send Test'}
            </button>
          </div>
        </div>

        <button onClick={saveWa} disabled={waSaving}
          className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${waSaved ? 'bg-green-500 text-white' : 'bg-green-600 hover:bg-green-700 text-white'} disabled:opacity-50`}>
          {waSaved ? <><CheckCircle size={15} /> Saved!</> : waSaving ? 'Saving…' : 'Save WhatsApp Settings'}
        </button>
      </div>
    </div>
  );
}
